import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Shuffle, RotateCw, CheckCircle, XCircle, Bookmark, Filter, Settings, Download, Tag, Activity } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'react-hot-toast';

const Flashcards = ({ flashcards }) => {
    const [currentCard, setCurrentCard] = useState(0);
    const [showAnswer, setShowAnswer] = useState(false);
    const [cards, setCards] = useState([]);
    const [isFlipping, setIsFlipping] = useState(false);
    const [studyMode, setStudyMode] = useState('all'); // 'all', 'marked', 'difficult'
    const [cardStates, setCardStates] = useState({});
    const [showSettings, setShowSettings] = useState(false);
    const [animation, setAnimation] = useState('flip'); // 'flip', 'slide', 'fade'
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [difficultyFilter, setDifficultyFilter] = useState('all');
    const [showFilters, setShowFilters] = useState(false);
    const cardRef = useRef(null);
    const { theme } = useTheme();

    // Initialize cards and card states
    useEffect(() => {
        if (flashcards && Array.isArray(flashcards)) {
            setCards(flashcards);
            // Initialize card states if not already set
            const initialStates = {};
            flashcards.forEach((card, index) => {
                if (!cardStates[index]) {
                    initialStates[index] = { marked: false, known: false, attempts: 0, lastSeen: null };
                }
            });
            setCardStates(prev => ({ ...prev, ...initialStates }));
        }
    }, [flashcards]);

    // Extract unique categories from cards
    const categories = ['all', ...new Set(cards.filter(card => card.category).map(card => card.category))];
    
    // Extract difficulties (ensure they are in order)
    const difficulties = ['all', 'basic', 'intermediate', 'advanced'].filter(diff => 
        diff === 'all' || cards.some(card => card.difficulty === diff)
    );

    // Filter cards based on all active filters
    const filteredCards = () => {
        let filtered = cards;
        
        // Study mode filtering
        if (studyMode === 'marked') {
            filtered = filtered.filter((_, i) => cardStates[i]?.marked);
        } else if (studyMode === 'difficult') {
            filtered = filtered.filter((_, i) => cardStates[i]?.attempts > 1 && !cardStates[i]?.known);
        }
        
        // Category filtering
        if (categoryFilter !== 'all') {
            filtered = filtered.filter(card => card.category === categoryFilter);
        }
        
        // Difficulty filtering
        if (difficultyFilter !== 'all') {
            filtered = filtered.filter(card => card.difficulty === difficultyFilter);
        }
        
        return filtered;
    };

    // Get the actual index in the original cards array
    const getOriginalIndex = (filteredIndex) => {
        const filtered = filteredCards();
        if (filtered.length === 0) return 0;
        const card = filtered[filteredIndex];
        return cards.findIndex(c => c === card);
    };

    const nextCard = () => {
        const filtered = filteredCards();
        if (currentCard < filtered.length - 1) {
            // Update last seen timestamp for the current card
            const originalIndex = getOriginalIndex(currentCard);
            updateCardState(originalIndex, { lastSeen: new Date().toISOString() });
            
            setCurrentCard(currentCard + 1);
            setShowAnswer(false);
            playAnimation();
        }
    };

    const prevCard = () => {
        if (currentCard > 0) {
            setCurrentCard(currentCard - 1);
            setShowAnswer(false);
            playAnimation();
        }
    };

    const handleFlip = () => {
        setIsFlipping(true);
        setShowAnswer(!showAnswer);
        setTimeout(() => setIsFlipping(false), 300);
        
        // If showing answer, increment attempt counter
        if (!showAnswer) {
            const originalIndex = getOriginalIndex(currentCard);
            updateCardState(originalIndex, { 
                attempts: (cardStates[originalIndex]?.attempts || 0) + 1 
            });
        }
    };

    const playAnimation = () => {
        if (animation === 'flip') {
            setIsFlipping(true);
            setTimeout(() => setIsFlipping(false), 300);
        } else if (animation === 'slide') {
            if (cardRef.current) {
                cardRef.current.classList.add('translate-x-full', 'opacity-0');
                setTimeout(() => {
                    cardRef.current?.classList.remove('translate-x-full', 'opacity-0');
                }, 300);
            }
        } else if (animation === 'fade') {
            if (cardRef.current) {
                cardRef.current.classList.add('opacity-0');
                setTimeout(() => {
                    cardRef.current?.classList.remove('opacity-0');
                }, 300);
            }
        }
    };

    // Mark card as known/unknown
    const markCardKnown = (known) => {
        const originalIndex = getOriginalIndex(currentCard);
        updateCardState(originalIndex, { known });
        toast.success(known ? 'Card marked as known!' : 'Card marked for review');
        
        // Auto advance to next card
        setTimeout(() => {
            if (currentCard < filteredCards().length - 1) {
                nextCard();
            }
        }, 500);
    };

    // Toggle bookmark status
    const toggleBookmark = () => {
        const originalIndex = getOriginalIndex(currentCard);
        updateCardState(originalIndex, { marked: !cardStates[originalIndex]?.marked });
        toast.success(
            cardStates[originalIndex]?.marked 
                ? 'Bookmark removed' 
                : 'Card bookmarked for later review'
        );
    };

    // Update a specific card's state
    const updateCardState = (index, newState) => {
        setCardStates(prev => ({
            ...prev,
            [index]: {
                ...prev[index],
                ...newState
            }
        }));
    };

    // Keyboard navigation
    useEffect(() => {
        const handleKeyPress = (e) => {
            if (e.key === 'ArrowLeft') prevCard();
            if (e.key === 'ArrowRight') nextCard();
            if (e.key === ' ') {
                e.preventDefault();
                handleFlip();
            }
            if (e.key === 'k') markCardKnown(true);
            if (e.key === 'u') markCardKnown(false);
            if (e.key === 'b') toggleBookmark();
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [currentCard, cards.length, showAnswer, cardStates]);

    const shuffleCards = () => {
        const shuffled = [...cards].sort(() => Math.random() - 0.5);
        setCards(shuffled);
        setCurrentCard(0);
        setShowAnswer(false);
        toast.success('Cards shuffled!');
    };

    // Reset all filters
    const resetFilters = () => {
        setCategoryFilter('all');
        setDifficultyFilter('all');
        setStudyMode('all');
        toast.success('Filters reset');
    };

    // Export flashcards to CSV
    const exportFlashcards = () => {
        const csvContent = 'data:text/csv;charset=utf-8,' + 
            'Front,Back,Category,Difficulty\n' + 
            cards.map(card => 
                `"${card.front.replace(/"/g, '""')}","${card.back.replace(/"/g, '""')}","${card.category || ''}","${card.difficulty || ''}"`
            ).join('\n');
        
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', 'flashcards.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success('Flashcards exported to CSV');
    };

    // Calculate study statistics
    const stats = {
        total: cards.length,
        studied: Object.values(cardStates).filter(state => state.attempts > 0).length,
        known: Object.values(cardStates).filter(state => state.known).length,
        bookmarked: Object.values(cardStates).filter(state => state.marked).length,
        byDifficulty: {
            basic: cards.filter(card => card.difficulty === 'basic').length,
            intermediate: cards.filter(card => card.difficulty === 'intermediate').length,
            advanced: cards.filter(card => card.difficulty === 'advanced').length
        },
        byCategory: categories.filter(cat => cat !== 'all').reduce((acc, cat) => {
            acc[cat] = cards.filter(card => card.category === cat).length;
            return acc;
        }, {})
    };
    
    if (!flashcards || !Array.isArray(flashcards) || flashcards.length === 0) {
        return (
            <div className="max-w-2xl mx-auto p-6 rounded-xl bg-theme border border-theme theme-transition">
                <div className="text-center py-12">
                    <p className="text-lg text-accent">
                        No flashcards available yet.
                    </p>
                </div>
            </div>
        );
    }

    const filtered = filteredCards();
    const noFilteredCards = filtered.length === 0;
    const originalIndex = noFilteredCards ? -1 : getOriginalIndex(currentCard);
    const currentCardState = originalIndex >= 0 ? cardStates[originalIndex] : null;
    const currentFilteredCard = !noFilteredCards ? filtered[currentCard] : null;

    // Get difficulty color and icon
    const getDifficultyColor = (difficulty) => {
        switch(difficulty) {
            case 'basic': return 'text-green-500';
            case 'intermediate': return 'text-yellow-500';
            case 'advanced': return 'text-red-500';
            default: return 'text-accent';
        }
    };

    return (
        <div className="h-full w-full flex flex-col items-center justify-center">
            {/* Study mode selector and settings */}
            <div className="w-full max-w-2xl mb-4 flex items-center justify-between">
                <div className="flex gap-2">
                    <button 
                        onClick={() => setStudyMode('all')} 
                        className={`px-3 py-1 rounded-md text-sm transition-colors ${
                            studyMode === 'all' 
                                ? 'bg-primary text-white' 
                                : 'bg-secondary text-accent hover:bg-primary/20'
                        }`}
                    >
                        All ({cards.length})
                    </button>
                    <button 
                        onClick={() => setStudyMode('marked')} 
                        className={`px-3 py-1 rounded-md text-sm transition-colors ${
                            studyMode === 'marked' 
                                ? 'bg-primary text-white' 
                                : 'bg-secondary text-accent hover:bg-primary/20'
                        }`}
                    >
                        Bookmarked ({stats.bookmarked})
                    </button>
                    <button 
                        onClick={() => setStudyMode('difficult')} 
                        className={`px-3 py-1 rounded-md text-sm transition-colors ${
                            studyMode === 'difficult' 
                                ? 'bg-primary text-white' 
                                : 'bg-secondary text-accent hover:bg-primary/20'
                        }`}
                    >
                        Difficult ({cards.filter((_, i) => cardStates[i]?.attempts > 1 && !cardStates[i]?.known).length})
                    </button>
                </div>
                <div className="flex gap-2">
                    <button 
                        onClick={() => setShowFilters(!showFilters)} 
                        className={`p-2 rounded-lg transition-colors hover:bg-secondary ${showFilters ? 'bg-primary/20' : ''}`}
                        title="Filter cards"
                    >
                        <Filter size={18} className="text-accent" />
                    </button>
                    <button 
                        onClick={() => setShowSettings(!showSettings)} 
                        className={`p-2 rounded-lg transition-colors hover:bg-secondary ${showSettings ? 'bg-primary/20' : ''}`}
                        title="Settings"
                    >
                        <Settings size={18} className="text-accent" />
                    </button>
                    <button 
                        onClick={exportFlashcards} 
                        className="p-2 rounded-lg transition-colors hover:bg-secondary"
                        title="Export to CSV"
                    >
                        <Download size={18} className="text-accent" />
                    </button>
                </div>
            </div>
            
            {/* Filters panel */}
            {showFilters && (
                <div className="w-full max-w-2xl mb-4 p-4 bg-theme border border-theme rounded-lg theme-transition">
                    <div className="flex justify-between items-center mb-3">
                        <h3 className="font-medium text-accent">Filters</h3>
                        <button 
                            onClick={resetFilters}
                            className="text-xs text-accent hover:text-primary transition-colors"
                        >
                            Reset all filters
                        </button>
                    </div>
                    
                    <div className="flex flex-col gap-3">
                        {/* Categories filter */}
                        {categories.length > 1 && (
                            <div>
                                <label className="text-sm text-accent flex items-center gap-1 mb-1">
                                    <Tag size={14} /> Category:
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {categories.map(category => (
                                        <button 
                                            key={category}
                                            onClick={() => setCategoryFilter(category)}
                                            className={`px-3 py-1 rounded-md text-xs transition-colors ${
                                                categoryFilter === category
                                                    ? 'bg-primary text-white' 
                                                    : 'bg-secondary text-accent hover:bg-primary/20'
                                            }`}
                                        >
                                            {category === 'all' ? 'All Categories' : category}
                                            {category !== 'all' && (
                                                <span className="ml-1 opacity-70">({stats.byCategory[category] || 0})</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        
                        {/* Difficulty filter */}
                        {difficulties.length > 1 && (
                            <div>
                                <label className="text-sm text-accent flex items-center gap-1 mb-1">
                                    <Activity size={14} /> Difficulty:
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {difficulties.map(difficulty => (
                                        <button 
                                            key={difficulty}
                                            onClick={() => setDifficultyFilter(difficulty)}
                                            className={`px-3 py-1 rounded-md text-xs transition-colors ${
                                                difficultyFilter === difficulty
                                                    ? 'bg-primary text-white' 
                                                    : 'bg-secondary text-accent hover:bg-primary/20'
                                            }`}
                                        >
                                            {difficulty === 'all' ? 'All Levels' : difficulty}
                                            {difficulty !== 'all' && (
                                                <span className="ml-1 opacity-70">
                                                    ({stats.byDifficulty[difficulty] || 0})
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
            
            {/* Settings panel */}
            {showSettings && (
                <div className="w-full max-w-2xl mb-4 p-4 bg-theme border border-theme rounded-lg theme-transition">
                    <h3 className="font-medium text-accent mb-2">Settings</h3>
                    <div className="flex flex-col gap-2">
                        <div>
                            <label className="text-sm text-accent">Card Animation:</label>
                            <div className="flex gap-2 mt-1">
                                <button 
                                    onClick={() => setAnimation('flip')} 
                                    className={`px-3 py-1 rounded-md text-sm ${animation === 'flip' ? 'bg-primary text-white' : 'bg-secondary text-accent'}`}
                                >
                                    Flip
                                </button>
                                <button 
                                    onClick={() => setAnimation('slide')} 
                                    className={`px-3 py-1 rounded-md text-sm ${animation === 'slide' ? 'bg-primary text-white' : 'bg-secondary text-accent'}`}
                                >
                                    Slide
                                </button>
                                <button 
                                    onClick={() => setAnimation('fade')} 
                                    className={`px-3 py-1 rounded-md text-sm ${animation === 'fade' ? 'bg-primary text-white' : 'bg-secondary text-accent'}`}
                                >
                                    Fade
                                </button>
                            </div>
                        </div>
                        <div className="mt-2">
                            <p className="text-sm text-accent">Study Statistics:</p>
                            <div className="grid grid-cols-2 gap-2 mt-1">
                                <div className="text-sm text-accent/80">Total Cards: <span className="font-medium">{stats.total}</span></div>
                                <div className="text-sm text-accent/80">Studied: <span className="font-medium">{stats.studied}</span></div>
                                <div className="text-sm text-accent/80">Known: <span className="font-medium">{stats.known}</span></div>
                                <div className="text-sm text-accent/80">Bookmarked: <span className="font-medium">{stats.bookmarked}</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="w-full max-w-2xl mx-auto p-6 rounded-xl bg-theme border border-theme theme-transition relative">
                {/* Progress bar */}
                <div className="w-full h-2 rounded-full mb-4 bg-secondary">
                    <div 
                        className="h-2 rounded-full transition-all duration-300 bg-primary"
                        style={{ width: noFilteredCards ? '0%' : `${((currentCard + 1) / filtered.length) * 100}%` }}
                    />
                </div>

                <div className="flex justify-between items-center mb-4">
                    <button
                        onClick={prevCard}
                        disabled={currentCard === 0 || noFilteredCards}
                        className={`
                            p-2 rounded-lg transition-colors
                            ${(currentCard === 0 || noFilteredCards)
                                ? 'opacity-50 cursor-not-allowed' 
                                : 'hover:bg-secondary'
                            }
                        `}
                    >
                        <ChevronLeft size={24} className="text-accent" />
                    </button>
                    <span className="text-accent">
                        {noFilteredCards 
                            ? 'No cards match filter' 
                            : `Card ${currentCard + 1} of ${filtered.length}`}
                    </span>
                    <button
                        onClick={nextCard}
                        disabled={currentCard === filtered.length - 1 || noFilteredCards}
                        className={`
                            p-2 rounded-lg transition-colors
                            ${(currentCard === filtered.length - 1 || noFilteredCards)
                                ? 'opacity-50 cursor-not-allowed'
                                : 'hover:bg-secondary'
                            }
                        `}
                    >
                        <ChevronRight size={24} className="text-accent" />
                    </button>
                </div>

                {/* Action buttons */}
                <div className="absolute top-6 right-6 flex gap-2">
                    <button
                        onClick={shuffleCards}
                        className="p-2 rounded-lg transition-colors hover:bg-secondary"
                        title="Shuffle cards"
                    >
                        <Shuffle size={20} className="text-accent" />
                    </button>
                    {!noFilteredCards && (
                        <button
                            onClick={toggleBookmark}
                            className={`p-2 rounded-lg transition-colors hover:bg-secondary ${
                                currentCardState?.marked ? 'text-yellow-500' : 'text-accent'
                            }`}
                            title={currentCardState?.marked ? "Remove bookmark" : "Bookmark this card"}
                        >
                            <Bookmark size={20} className={currentCardState?.marked ? "fill-yellow-500" : ""} />
                        </button>
                    )}
                </div>

                {noFilteredCards ? (
                    <div className="min-h-[200px] p-6 rounded-lg bg-secondary/50 flex items-center justify-center">
                        <p className="text-accent text-center">
                            No cards match the current filter.
                            <br />
                            <button 
                                onClick={resetFilters} 
                                className="mt-2 px-3 py-1 rounded-md text-sm bg-primary text-white"
                            >
                                Reset all filters
                            </button>
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Card metadata - category and difficulty */}
                        {(currentFilteredCard?.category || currentFilteredCard?.difficulty) && (
                            <div className="flex justify-start gap-2 mb-2">
                                {currentFilteredCard?.category && (
                                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-secondary text-accent">
                                        <Tag size={12} />
                                        {currentFilteredCard.category}
                                    </span>
                                )}
                                {currentFilteredCard?.difficulty && (
                                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-secondary ${getDifficultyColor(currentFilteredCard.difficulty)}`}>
                                        <Activity size={12} />
                                        {currentFilteredCard.difficulty}
                                    </span>
                                )}
                            </div>
                        )}
                        
                        {/* Card display with animation */}
                        <div 
                            ref={cardRef}
                            onClick={handleFlip}
                            className={`
                                min-h-[250px] p-6 rounded-lg cursor-pointer
                                flex items-center justify-center text-center
                                transform transition-all duration-300
                                ${isFlipping ? 'scale-95' : 'scale-100'}
                                ${animation === 'slide' ? 'transition-transform' : ''}
                                ${animation === 'fade' ? 'transition-opacity' : ''}
                                bg-secondary hover:bg-primary/10
                                ${currentCardState?.known ? 'border-l-4 border-green-500' : ''}
                                ${currentCardState?.attempts > 1 && !currentCardState?.known ? 'border-l-4 border-yellow-500' : ''}
                                ${currentFilteredCard?.difficulty === 'advanced' ? 'border-r-4 border-red-500/50' : ''}
                                ${currentFilteredCard?.difficulty === 'intermediate' ? 'border-r-4 border-yellow-500/50' : ''}
                                ${currentFilteredCard?.difficulty === 'basic' ? 'border-r-4 border-green-500/50' : ''}
                            `}
                        >
                            <div className={`
                                text-lg transition-opacity duration-300
                                ${isFlipping ? 'opacity-0' : 'opacity-100'}
                                text-theme
                            `}>
                                {showAnswer 
                                    ? filtered[currentCard]?.back
                                    : filtered[currentCard]?.front}
                            </div>
                        </div>

                        {/* Knowledge assessment */}
                        {showAnswer && (
                            <div className="flex justify-center mt-4 gap-3">
                                <button
                                    onClick={() => markCardKnown(false)}
                                    className="flex items-center gap-1 px-4 py-2 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                                >
                                    <XCircle size={18} />
                                    <span>Still learning (U)</span>
                                </button>
                                <button
                                    onClick={() => markCardKnown(true)}
                                    className="flex items-center gap-1 px-4 py-2 rounded-lg bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
                                >
                                    <CheckCircle size={18} />
                                    <span>Got it! (K)</span>
                                </button>
                            </div>
                        )}

                        <div className="text-center mt-4">
                            <p className="text-sm text-accent">
                                Click card or press spacebar to {showAnswer ? 'show front' : 'reveal back'}
                            </p>
                            <p className="text-xs mt-1 text-accent/80">
                                Use ← → arrow keys to navigate • B to bookmark • K/U to mark as known/unknown
                            </p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Flashcards; 